'use client';

import { forwardRef, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import CloseButton from '../primitives/CloseButton';
import Button from '../actions/Button';
import styles from './Toast.module.css';
import Icon from '../primitives/Icon';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface ToastProps {
  type?: ToastType;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  duration?: number;
  open?: boolean;
  onClose?: () => void;
  className?: string;
}

const typeIcons: Record<ToastType, string> = {
  info: 'info',
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
};

const Toast = forwardRef<HTMLDivElement, ToastProps>(({
  type = 'info',
  title,
  description,
  action,
  duration = 5000,
  open = true,
  onClose,
  className = '',
}, ref) => {
  const [visible, setVisible] = useState(open);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      onClose?.();
    }, 280);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!visible || duration <= 0) return;
    timerRef.current = setTimeout(handleClose, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, duration, handleClose]);

  if (!visible) return null;

  const classNames = [
    styles.toast,
    styles[type],
    exiting ? styles.exiting : styles.entering,
    description ? styles.multiline : styles.singleline,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      className={classNames}
      role="alert"
      aria-live="polite"
      onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
      tabIndex={0}
    >
      <Icon name={typeIcons[type]} size="md" className={styles.icon} />
      <div className={styles.content}>
        <span className={styles.title}>{title}</span>
        {description && <span className={styles.description}>{description}</span>}
      </div>
      {action && (
        <Button
          variant="text"
          size="sm"
          className={styles.action}
          onClick={action.onClick}
          type="button"
        >
          {action.label}
        </Button>
      )}
      <CloseButton className={styles.close} onClick={handleClose} aria-label="Close notification" size="sm" />
      {duration > 0 && (
        <div className={styles.progressTrack}>
          <div
            ref={progressRef}
            className={styles.progressBar}
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      )}
    </div>
  );
});

Toast.displayName = 'Toast';
export default Toast;

/* ===== Toast Container ===== */

interface ToastItem {
  id: string;
  props: Omit<ToastProps, 'open'>;
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  return (
    <div className={styles.container} aria-live="polite">
      {toasts.map((t) => (
        <Toast key={t.id} {...t.props} open onClose={() => onRemove(t.id)} />
      ))}
    </div>
  );
}
